import { useEffect, useState } from "react";
import { generateAds, listAds, deleteAd, listProducts } from "@/lib/api";
import { Sparkle, Copy, Trash, CircleNotch, InstagramLogo, TiktokLogo, RedditLogo, PinterestLogo, TwitterLogo, FacebookLogo, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLATFORMS = [
  { id: "pinterest", label: "Pinterest", icon: PinterestLogo, color: "#e60023" },
  { id: "reddit",    label: "Reddit",    icon: RedditLogo,    color: "#ff4500" },
  { id: "twitter",   label: "X / Twitter", icon: TwitterLogo,  color: "#ffffff" },
  { id: "facebook",  label: "Facebook",  icon: FacebookLogo,  color: "#1877f2" },
  { id: "instagram", label: "Instagram", icon: InstagramLogo, color: "#e4405f" },
  { id: "tiktok",    label: "TikTok",    icon: TiktokLogo,    color: "#69c9d0" },
];

const TONES = ["energetic", "professional", "funny", "luxury", "urgent"];

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch { toast.error("Copy failed"); }
};

const PlatformCard = ({ platformId, data }) => {
  const p = PLATFORMS.find(x => x.id === platformId);
  if (!p || !data) return null;
  const Icon = p.icon;
  const fullText = [data.title, data.body, (data.hashtags || []).join(" ")].filter(Boolean).join("\n\n");
  return (
    <div className="border border-arena-border bg-arena-surface hover:border-arena-primary transition-colors" data-testid={`ad-card-${platformId}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-arena-border">
        <div className="flex items-center gap-2">
          <Icon size={18} weight="fill" style={{ color: p.color }} />
          <span className="font-heading text-sm font-semibold uppercase tracking-wider">{p.label}</span>
        </div>
        <button
          onClick={() => copyToClipboard(fullText, p.label)}
          className="text-arena-muted hover:text-arena-primary flex items-center gap-1 text-[10px] tracking-widest uppercase"
          data-testid={`copy-${platformId}`}
        >
          <Copy size={12} weight="bold" /> Copy All
        </button>
      </div>
      <div className="p-4 space-y-3 text-xs">
        {data.title && (
          <div>
            <div className="overline mb-1">Title / Hook</div>
            <div className="text-arena-text">{data.title}</div>
          </div>
        )}
        {data.body && (
          <div>
            <div className="overline mb-1">Body</div>
            <div className="text-arena-text whitespace-pre-wrap leading-relaxed">{data.body}</div>
          </div>
        )}
        {data.hashtags?.length > 0 && (
          <div>
            <div className="overline mb-1">Hashtags</div>
            <div className="text-arena-primary flex flex-wrap gap-1">
              {data.hashtags.map((h, i) => <span key={i}>{h}</span>)}
            </div>
          </div>
        )}
        {data.extra && (
          <div>
            <div className="overline mb-1">Notes</div>
            <div className="text-arena-warning">{data.extra}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdGenerator() {
  const [form, setForm] = useState({
    product_name: "", product_description: "", price: "",
    target_audience: "", tone: "energetic",
    platforms: ["pinterest", "reddit", "twitter", "facebook", "instagram"],
  });
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [pickedProductId, setPickedProductId] = useState("");

  const loadHistory = async () => {
    const h = await listAds();
    setHistory(h);
  };
  const loadProducts = async () => {
    const p = await listProducts();
    setProducts(p);
  };

  useEffect(() => { loadHistory(); loadProducts(); }, []);

  const pickProduct = (id) => {
    setPickedProductId(id);
    if (!id) return;
    const p = products.find(x => x.id === id);
    if (!p) return;
    setForm(f => ({
      ...f,
      product_name: p.name,
      product_description: p.description || "",
      price: `$${p.price?.toFixed?.(2) || p.price}`,
    }));
    toast.success(`Loaded: ${p.name}`);
  };

  const togglePlatform = (id) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(id) ? f.platforms.filter(x => x !== id) : [...f.platforms, id]
    }));
  };

  const handleGenerate = async () => {
    if (!form.product_name) {
      toast.error("Product name is required");
      return;
    }
    if (form.platforms.length === 0) {
      toast.error("Pick at least one platform");
      return;
    }
    setLoading(true);
    try {
      const res = await generateAds(form);
      setCurrent(res);
      toast.success("Ads generated");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadPrevious = (camp) => {
    setCurrent(camp);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this campaign?")) return;
    await deleteAd(id);
    loadHistory();
  };

  return (
    <div className="p-8" data-testid="ads-page">
      <div className="mb-6">
        <div className="overline mb-2">// ad forge</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">FREE AD <span className="text-arena-primary">GENERATOR</span></h1>
        <p className="text-arena-muted mt-2 text-sm">Generate authentic organic post copy for Pinterest, Reddit, X, Facebook, Instagram & TikTok. Copy → paste. Zero ad spend.</p>
      </div>

      {/* Input Form */}
      <div className="border border-arena-border bg-arena-surface p-6 mb-6 relative overflow-hidden" data-testid="ad-form">
        {/* Tracing beam scan */}
        <div className="absolute inset-y-0 left-0 w-full pointer-events-none opacity-20">
          <div className="w-full h-8 bg-gradient-to-b from-transparent via-arena-primary to-transparent animate-scan" />
        </div>
        <div className="relative">
          <div className="mb-4">
            <label className="overline block mb-1 flex items-center gap-2"><Package size={12} weight="bold" className="text-arena-primary" /> Load from Product (optional)</label>
            <select
              data-testid="ad-product-picker"
              value={pickedProductId}
              onChange={e => pickProduct(e.target.value)}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
            >
              <option value="">— Choose a product to auto-fill —</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.name} — ${p.price?.toFixed?.(2)}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-1">Product Name *</label>
              <input
                data-testid="ad-input-product"
                value={form.product_name}
                onChange={e => setForm({ ...form, product_name: e.target.value })}
                placeholder="e.g. Wireless LED Strip Lights"
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              />
            </div>
            <div>
              <label className="overline block mb-1">Price</label>
              <input
                data-testid="ad-input-price"
                value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
                placeholder="$29.99"
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-1">Product Description</label>
              <textarea
                data-testid="ad-input-description"
                value={form.product_description}
                onChange={e => setForm({ ...form, product_description: e.target.value })}
                placeholder="What does it do? What makes it special?"
                rows={3}
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none resize-none"
              />
            </div>
            <div>
              <label className="overline block mb-1">Target Audience</label>
              <input
                data-testid="ad-input-audience"
                value={form.target_audience}
                onChange={e => setForm({ ...form, target_audience: e.target.value })}
                placeholder="Gen Z gamers, home decorators..."
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              />
            </div>
            <div>
              <label className="overline block mb-1">Tone</label>
              <select
                data-testid="ad-input-tone"
                value={form.tone}
                onChange={e => setForm({ ...form, tone: e.target.value })}
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              >
                {TONES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-5">
            <label className="overline block mb-2">Platforms</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map(p => {
                const active = form.platforms.includes(p.id);
                const Icon = p.icon;
                return (
                  <button
                    key={p.id}
                    onClick={() => togglePlatform(p.id)}
                    data-testid={`platform-toggle-${p.id}`}
                    className={`border px-3 py-2 text-[11px] tracking-widest uppercase flex items-center gap-2 transition-all ${
                      active
                        ? 'bg-arena-primary text-black border-arena-primary font-bold'
                        : 'border-arena-border text-arena-muted hover:text-arena-primary hover:border-arena-primary'
                    }`}
                  >
                    <Icon size={14} weight="fill" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={loading}
            data-testid="generate-btn"
            className="mt-5 bg-arena-primary text-black px-6 py-3 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
            {loading ? "Forging..." : "Generate Ads"}
          </button>
        </div>
      </div>

      {/* Results */}
      {current && (
        <div className="mb-8" data-testid="ad-results">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="overline">// output</div>
              <h2 className="font-heading text-2xl font-bold mt-1">{current.product_name}</h2>
            </div>
            <div className="text-xs text-arena-muted">{current.tone} • {current.platforms.length} platforms</div>
          </div>
          {current.content?.error ? (
            <div className="border border-arena-danger bg-arena-surface p-4 text-arena-danger text-xs">
              {current.content.error}
              <pre className="mt-2 text-arena-muted whitespace-pre-wrap">{current.content.raw?.slice(0, 500)}</pre>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(current.platforms || []).map(p => (
                <PlatformCard key={p} platformId={p} data={current.content?.[p]} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div>
        <div className="overline mb-2">// campaign history</div>
        <div className="border border-arena-border bg-arena-surface divide-y divide-arena-border">
          {history.length === 0 && (
            <div className="p-6 text-center text-arena-muted text-xs">No campaigns yet.</div>
          )}
          {history.map(h => (
            <div key={h.id} className="p-4 flex items-center gap-4 hover:bg-arena-surfaceHover" data-testid={`history-row-${h.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{h.product_name}</div>
                <div className="text-[10px] text-arena-muted">{h.tone} • {h.platforms?.join(", ")}</div>
              </div>
              <button
                onClick={() => handleLoadPrevious(h)}
                data-testid={`load-${h.id}`}
                className="text-xs tracking-widest uppercase text-arena-primary hover:underline"
              >
                Load →
              </button>
              <button
                onClick={() => handleDelete(h.id)}
                data-testid={`delete-history-${h.id}`}
                className="text-arena-danger hover:text-red-400"
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
